import { useEffect, useState, useMemo } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search as SearchIcon, Loader2, ShieldCheck, Wrench, Lock, ArrowRight } from "lucide-react";

const FIELDS = [
  ["oem", "OEM number", "Sets the OEM number on the selected parts."],
  ["interchange", "Interchange number", "Appends an interchange number (additive — existing numbers are kept)."],
  ["superseded", "Superseded number", "Appends a superseding number (additive — existing numbers are kept)."],
];

// Mirrors backend normalize_oem: ignore spaces / - . _ / and case.
const norm = (v) => (v || "").toString().replace(/[\s\-._/]/g, "").toUpperCase();
const icList = (p) => (p.interchange_numbers || []).map((x) => (x || {}).number).filter(Boolean);
const ssList = (p) => (p.superseded_numbers || []).filter(Boolean);

export default function BulkFixPage() {
  const navigate = useNavigate();
  const [q, setQ] = useBackState("q", "");
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState({}); // {id: true}

  const [field, setField] = useState("oem");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const run = async () => {
    if (!q.trim()) { setParts([]); setSearched(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/parts", { params: { q: q.trim(), limit: 300 } });
      setParts(data);
      setSearched(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => { if (q.trim()) run(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const selectedParts = useMemo(() => parts.filter((p) => selected[p.id]), [parts, selected]);
  const allSelected = parts.length > 0 && parts.every((p) => selected[p.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(parts.map((p) => [p.id, true])));
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // before -> after preview per selected part (client-side, matches backend rules)
  const preview = useMemo(() => {
    const vn = norm(value);
    return selectedParts.map((p) => {
      let before, after, willChange;
      if (field === "oem") {
        before = (p.oem_number || "").trim();
        willChange = norm(before) !== vn && !!vn;
        after = value.trim();
      } else if (field === "interchange") {
        const cur = icList(p);
        willChange = !!vn && !cur.some((x) => norm(x) === vn);
        before = cur.join(", ");
        after = willChange ? [...cur, value.trim()].join(", ") : before;
      } else {
        const cur = ssList(p);
        willChange = !!vn && !cur.some((x) => norm(x) === vn);
        before = cur.join(", ");
        after = willChange ? [...cur, value.trim()].join(", ") : before;
      }
      return { p, before: before || "—", after: after || "—", willChange };
    });
  }, [selectedParts, field, value]);

  const willChangeCount = preview.filter((r) => r.willChange).length;
  const fieldLabel = FIELDS.find((f) => f[0] === field)?.[1] || field;

  const openReview = () => {
    if (!selectedParts.length) { toast.error("Select at least one part."); return; }
    if (!value.trim()) { toast.error("Enter the number to apply."); return; }
    if (!reason.trim()) { toast.error("A shared reason is required."); return; }
    setConfirmOpen(true);
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/parts/bulk-fix", {
        part_ids: selectedParts.map((p) => p.id), field, value: value.trim(), reason: reason.trim(),
      });
      toast.success(`Batch ${data.batch_id}: ${data.changed_count} changed, ${data.unchanged_count} already had it, ${data.skipped_count} skipped.`);
      setConfirmOpen(false);
      setSelected({}); setValue(""); setReason("");
      run();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid="bulk-fix-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-[#B01E28]" />
        <h1 className="font-display text-2xl font-bold tracking-tight">Bulk-Fix Part Numbers</h1>
        <Badge className="rounded-md bg-[#B01E28] text-white text-[9px] uppercase font-mono ml-1">Admin</Badge>
      </div>
      <p className="text-sm text-slate-500 mb-4 max-w-3xl">
        Search, select parts, and add one number (OEM / interchange / superseded) to all of them at once — the "fix" half of find-and-fix.
        Additive and findability-only: this <b>never</b> touches the donor tie, status, value, or major/minor. Every part gets its own logged before→after under a shared batch.
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="bulkfix-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search parts — name, OEM, notes, category, interchange…" className="rounded-lg border-slate-200 pl-9" />
        </div>
        {loading && <span className="inline-flex items-center text-xs font-mono text-slate-400"><Loader2 className="w-4 h-4 animate-spin mr-1" />searching…</span>}
      </div>

      {/* Results */}
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-[40px_100px_1fr_150px_150px] gap-2 bg-slate-50 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100 items-center">
          <span><Checkbox data-testid="bulkfix-select-all" checked={allSelected} onCheckedChange={toggleAll} disabled={!parts.length} /></span>
          <span>Code</span><span>Part / brand</span><span>OEM #</span><span>Interchange / superseded</span>
        </div>
        {!searched ? <div className="p-10 text-center font-mono text-xs text-slate-400">SEARCH TO BEGIN</div>
          : parts.length === 0 ? <div className="p-10 text-center font-mono text-xs text-slate-400">NO MATCHES</div>
          : parts.map((p) => (
            <div key={p.id} data-testid={`bulkfix-row-${p.short_code}`}
              className={`grid grid-cols-[40px_100px_1fr_150px_150px] gap-2 px-4 py-3 border-t border-slate-100 items-center text-sm ${selected[p.id] ? "bg-amber-50/60" : "hover:bg-slate-50/70"}`}>
              <Checkbox data-testid={`bulkfix-check-${p.short_code}`} checked={!!selected[p.id]} onCheckedChange={() => toggleOne(p.id)} />
              <span className="font-mono text-xs font-bold text-[#B01E28] cursor-pointer hover:underline" onClick={() => navigate(`/parts/${p.id}`)}>{p.tag_code || p.short_code}</span>
              <span><span className="font-medium">{p.name}</span> <span className="font-mono text-[10px] text-slate-400 block">{p.brand}{p.category_name ? ` · ${p.category_name}` : ""}</span></span>
              <span className="font-mono text-[11px] text-slate-600 truncate">{p.oem_number || "—"}</span>
              <span className="font-mono text-[10px] text-slate-500 truncate">{[icList(p).join(", "), ssList(p).join(", ")].filter(Boolean).join(" | ") || "—"}</span>
            </div>
          ))}
      </div>

      {/* Action bar */}
      {selectedParts.length > 0 && (
        <div data-testid="bulkfix-action-bar" className="sticky bottom-4 mt-4 rounded-xl border border-slate-200 bg-white shadow-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-[#E0781A]" />
            <span className="font-display font-bold" data-testid="bulkfix-selected-count">{selectedParts.length} part{selectedParts.length > 1 ? "s" : ""} selected</span>
            <Lock className="w-3.5 h-3.5 text-slate-300 ml-2" /><span className="text-[11px] text-slate-400">donor tie & status never change</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Field to fix</label>
              <Select value={field} onValueChange={setField}>
                <SelectTrigger data-testid="bulkfix-field" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">{FIELDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Number to apply</label>
              <Input data-testid="bulkfix-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. A1234-56" className="rounded-lg border-slate-200 mt-1 font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Shared reason (logged on every part)</label>
              <Input data-testid="bulkfix-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Found OEM# for these door pads" className="rounded-lg border-slate-200 mt-1" />
            </div>
            <Button data-testid="bulkfix-review" onClick={openReview} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-10">Review change</Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">{FIELDS.find((f) => f[0] === field)?.[2]}</p>
        </div>
      )}

      {/* Confirm (print-confirm pattern): exact N parts + change + reason BEFORE write */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-2xl" data-testid="bulkfix-confirm">
          <DialogHeader>
            <DialogTitle className="font-display">Confirm bulk fix</DialogTitle>
            <DialogDescription>
              Adding <b>{fieldLabel}</b> <span className="font-mono">"{value.trim()}"</span> — <b data-testid="bulkfix-confirm-change-count">{willChangeCount}</b> of {selectedParts.length} selected will change
              {willChangeCount !== selectedParts.length && <span> ({selectedParts.length - willChangeCount} already have it and stay untouched)</span>}.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900">
            <span className="font-bold uppercase tracking-wider">Reason (logged on every part):</span> <span data-testid="bulkfix-confirm-reason">{reason.trim()}</span>
          </div>
          <div className="max-h-[45dvh] overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
            {preview.map(({ p, before, after, willChange }) => (
              <div key={p.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${willChange ? "" : "opacity-50"}`} data-testid={`bulkfix-confirm-row-${p.short_code}`}>
                <span className="font-mono text-xs font-bold w-24 text-[#B01E28]">{p.tag_code || p.short_code}</span>
                <span className="flex-1 min-w-0"><span className="font-medium">{p.name}</span></span>
                <span className="font-mono text-[11px] text-slate-400 truncate max-w-[130px]">{before}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                <span className={`font-mono text-[11px] truncate max-w-[130px] ${willChange ? "text-emerald-700 font-bold" : "text-slate-400"}`}>{willChange ? after : "no change"}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="bulkfix-apply" disabled={applying || willChangeCount === 0} onClick={apply} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Apply to ${willChangeCount} part${willChangeCount !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
